import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    useToast,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Spinner,
    Input,
    InputGroup,
    InputLeftElement,
    Button,
    Collapse,
    Badge,
    Stat,
    StatLabel,
    StatNumber,
    IconButton,
    Tooltip,
    Icon,
    Divider,
} from '@chakra-ui/react';
import { SearchIcon, ChevronDownIcon, ChevronUpIcon, DownloadIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { Tooltip as RechartsTooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import Navbar from '../compnents/Navbar';
import SideBar from '../compnents/SideBar';
import Footer from '../compnents/Footer';
import AuthContext from '../context/AuthContext';
import ThemeContext from '../context/ThemeContext';
import FeatureUnlockContext from '../context/FeatureUnlockContext';
import UploadYourFile from './UploadYourFile';
import Loading from '../compnents/Loading';
import { FaLock, FaShieldAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function ReportsExports() {
    const { token, user, setUser, logout } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const { resetUnlocks } = useContext(FeatureUnlockContext);
    const [loading, setLoading] = useState(true);
    const [customers, setCustomers] = useState([]);
    const [pagination, setPagination] = useState({ total_count: 0, page: 1, page_size: 25 });
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCustomer, setExpandedCustomer] = useState(null);
    const [customerReports, setCustomerReports] = useState({});
    const [loadingReports, setLoadingReports] = useState({});
    const [downloadingPDF, setDownloadingPDF] = useState({});
    const [exportingCSV, setExportingCSV] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';
    const backgroundColor = themeData?.colors?.background || '#F7FAFC';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Theme-aware colors
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.600';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800';
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';
    const inputBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'white';
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const chartGridColor = isDarkTheme ? 'rgba(66, 153, 225, 0.2)' : '#E2E8F0';
    const chartAxisColor = isDarkTheme ? '#CBD5E0' : '#4A5568';
    const successColor = isDarkTheme ? '#68D391' : 'green.500';
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'rgba(232, 244, 255, 0.8)';
    const successBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'gray.50';
    const placeholderBg = isDarkTheme ? 'rgba(45, 55, 72, 0.3)' : 'gray.50';
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;
    const overlayBg = isDarkTheme ? 'rgba(26, 32, 44, 0.92)' : 'rgba(255, 255, 255, 0.92)';
    const radarStroke = isDarkTheme ? accentColor : primaryColor;
    const radarFill = isDarkTheme ? accentColor : primaryColor;
    const tooltipBg = isDarkTheme ? 'rgba(45, 55, 72, 0.95)' : '#fff';
    const tooltipBorder = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : '#E2E8F0';
    const hoverBg = isDarkTheme ? 'rgba(66, 153, 225, 0.08)' : 'gray.50';

    // Fetch user data
    useEffect(() => {
        const fetchUser = async () => {
            if (!token || user) {
                setLoading(false);
                return;
            }

            try {
                const res = await axios.get(`${API_BASE_URL}/auth/check-user`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (res.status === 420) {
                    console.error('Invalid token');
                    resetUnlocks();
                    logout();
                    return;
                }

                setUser(res.data);
            } catch (error) {
                console.error('Error fetching user:', error);
                if (error.response?.status === 420) {
                    console.error('Token expired or invalid, logging out...');
                    resetUnlocks();
                    logout();
                }
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, [token, user, setUser, logout, resetUnlocks]);

    // Fetch customers list
    const fetchCustomers = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const params = { page, page_size: 25 };
            if (search) params.search = search;

            const res = await axios.get(`${API_BASE_URL}/content/list-customers`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            setCustomers(res.data.customers);
            setPagination(res.data.pagination);
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to load customers.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    // Initial load customers
    useEffect(() => {
        if (user && user.noOfFileUploads > 0 && (user.subscription === 'pro' || user.subscription === 'business') && user.role === 'admin') {
            fetchCustomers();
        }
        // eslint-disable-next-line
    }, [user]);

    // Fetch customer bias report
    const fetchCustomerBiasReport = async (customerId) => {
        if (customerReports[customerId]) return; // Already loaded

        try {
            setLoadingReports(prev => ({ ...prev, [customerId]: true }));
            const res = await axios.get(`${API_BASE_URL}/content/admin-cutomer-bias-report/${customerId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setCustomerReports(prev => ({ ...prev, [customerId]: res.data }));
        } catch (error) {
            toast({
                title: 'Error',
                description: `Failed to load bias report for customer ${customerId}.`,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoadingReports(prev => ({ ...prev, [customerId]: false }));
        }
    };

    // Toggle customer report view
    const toggleCustomer = (customerId) => {
        if (expandedCustomer === customerId) {
            setExpandedCustomer(null);
        } else {
            setExpandedCustomer(customerId);
            fetchCustomerBiasReport(customerId);
        }
    };

    // Handle search
    const handleSearch = () => {
        fetchCustomers(1, searchTerm);
    };

    // Download customer PDF report
    const downloadCustomerPDFReport = async (customerId) => {
        try {
            setDownloadingPDF(prev => ({ ...prev, [customerId]: true }));
            const res = await axios.get(`${API_BASE_URL}/content/admin-export-customer-report/${customerId}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `customer_${customerId}_bias_report.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast({
                title: 'Success',
                description: `Customer report downloaded successfully.`,
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: `Failed to download report for customer ${customerId}.`,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setDownloadingPDF(prev => ({ ...prev, [customerId]: false }));
        }
    };

    // Export all customers CSV
    const exportAllCustomersCSV = async () => {
        try {
            setExportingCSV(true);
            const res = await axios.get(`${API_BASE_URL}/content/admin-cum-rm-export-customers`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'all-customers.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast({
                title: 'Success',
                description: 'All customers exported successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to export customers CSV.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setExportingCSV(false);
        }
    };

    // Export my customers CSV (for RM)
    const exportMyCustomersCSV = async () => {
        try {
            setExportingCSV(true);
            const res = await axios.get(`${API_BASE_URL}/content/admin-cum-rm-export-customers?rm_only=true`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'my-customers.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast({
                title: 'Success',
                description: 'Your customers exported successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to export your customers CSV.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setExportingCSV(false);
        }
    };

    // Export all bias scores CSV
    const exportBiasScoresCSV = async () => {
        try {
            setExportingCSV(true);
            const res = await axios.get(`${API_BASE_URL}/content/admin-export-bias-scores`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'bias_scores.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast({
                title: 'Success',
                description: 'Bias scores exported successfully.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top',
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to export bias scores CSV.',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setExportingCSV(false);
        }
    };

    // Get color for bias score
    const getScoreColor = (score) => {
        if (isDarkTheme) {
            // Darker theme colors for heatmap - adjusted for better visibility
            if (score >= 80) return '#7C0000';
            if (score >= 70) return '#A52A2A';
            if (score >= 60) return '#CD5C5C';
            if (score >= 50) return '#E67E22';
            if (score >= 40) return '#F39C12';
            if (score >= 30) return '#F1C40F';
            if (score >= 20) return '#9ACD32';
            if (score >= 10) return '#66BB6A';
            return '#43A047';
        } else {
            // Light theme colors
            if (score >= 80) return '#8B0000';
            if (score >= 70) return '#DC143C';
            if (score >= 60) return '#FF6347';
            if (score >= 50) return '#FF8C00';
            if (score >= 40) return '#FFA500';
            if (score >= 30) return '#FFD700';
            if (score >= 20) return '#FFFFE0';
            if (score >= 10) return '#90EE90';
            return '#228B22';
        }
    };

    // Get risk alignment color
    const getRiskAlignmentColor = (status) => {
        if (status === 'high' || status === 'good') return 'green';
        if (status === 'medium') return 'yellow';
        return 'red';
    };

    // Render radar chart
    const renderRadarChart = (radar) => {
        const radarData = Object.entries(radar).map(([key, value]) => ({
            category: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            score: value,
        }));

        return (
            <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData}>
                    <PolarGrid stroke={chartGridColor} />
                    <PolarAngleAxis
                        dataKey="category"
                        stroke={chartAxisColor}
                        tick={{ fontSize: 10, fill: chartAxisColor }}
                    />
                    <PolarRadiusAxis
                        angle={90}
                        domain={[0, 100]}
                        stroke={chartAxisColor}
                        tick={false}
                        axisLine={false}
                    />
                    <Radar
                        name="Score"
                        dataKey="score"
                        stroke={radarStroke}
                        fill={radarFill}
                        fillOpacity={0.6}
                    />
                    <RechartsTooltip
                        contentStyle={{
                            backgroundColor: tooltipBg,
                            border: `1px solid ${tooltipBorder}`,
                            borderRadius: '8px',
                            color: bodyTextColor
                        }}
                    />
                </RadarChart>
            </ResponsiveContainer>
        );
    };

    // Render bias heatmap
    const renderBiasHeatmap = (biases) => {
        const biasEntries = Object.entries(biases);
        const cols = 5;
        const rows = Math.ceil(biasEntries.length / cols);

        return (
            <Box
                bg={successBg}
                p={4}
                borderRadius="md"
                border="1px solid"
                borderColor={borderColor}
                overflow="auto"
            >
                <Flex justify="flex-end" mb={4} align="center">
                    <Text fontSize="xs" fontWeight="bold" mr={2} color={labelTextColor}>Score:</Text>
                    <HStack spacing={0}>
                        {[0, 20, 40, 60, 80, 100].map((val) => (
                            <Box key={val} textAlign="center">
                                <Box
                                    w="30px"
                                    h="15px"
                                    bg={getScoreColor(val)}
                                    border="1px solid"
                                    borderColor={borderColor}
                                />
                                <Text fontSize="9px" color={mutedTextColor}>{val}</Text>
                            </Box>
                        ))}
                    </HStack>
                </Flex>

                <Box display="inline-block" border="1px solid" borderColor={borderColor} borderRadius="md" overflow="hidden">
                    {Array.from({ length: rows }).map((_, rowIndex) => (
                        <Flex key={rowIndex}>
                            {Array.from({ length: cols }).map((_, colIndex) => {
                                const index = rowIndex * cols + colIndex;
                                if (index >= biasEntries.length) {
                                    return (
                                        <Box
                                            key={colIndex}
                                            w="140px"
                                            h="80px"
                                            bg={placeholderBg}
                                            borderRight="1px solid"
                                            borderBottom="1px solid"
                                            borderColor={borderColor}
                                        />
                                    );
                                }

                                const [key, value] = biasEntries[index];
                                const biasName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                                return (
                                    <Tooltip
                                        key={colIndex}
                                        label={
                                            <Box>
                                                <Text fontWeight="bold">{biasName}</Text>
                                                <Text>Score: {value.score}</Text>
                                                <Text>Confidence: {(value.confidence * 100).toFixed(0)}%</Text>
                                            </Box>
                                        }
                                        placement="top"
                                        hasArrow
                                    >
                                        <Box
                                            w="140px"
                                            h="80px"
                                            bg={getScoreColor(value.score)}
                                            borderRight="1px solid"
                                            borderBottom="1px solid"
                                            borderColor={borderColor}
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            justifyContent="center"
                                            cursor="pointer"
                                            transition="all 0.2s"
                                            position="relative"
                                            _hover={{ transform: 'scale(1.05)', zIndex: 10, boxShadow: '0 0 10px rgba(0,0,0,0.3)' }}
                                        >
                                            <Text
                                                fontSize="xs"
                                                fontWeight="bold"
                                                color={value.score > 50 ? "white" : "gray.800"}
                                                textAlign="center"
                                                px={2}
                                                noOfLines={2}
                                                lineHeight="1.2"
                                            >
                                                {biasName}
                                            </Text>
                                            <Text
                                                fontSize="2xl"
                                                fontWeight="bold"
                                                color={value.score > 50 ? "white" : "gray.800"}
                                                mt={1}
                                            >
                                                {value.score}
                                            </Text>
                                            <Badge
                                                colorScheme={value.confidence >= 0.8 ? "green" : "yellow"}
                                                fontSize="8px"
                                                position="absolute"
                                                top={1}
                                                right={1}
                                            >
                                                {(value.confidence * 100).toFixed(0)}%
                                            </Badge>
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Flex>
                    ))}
                </Box>
            </Box>
        );
    };

    // Render product recommendation
    const renderProductRecommendation = (productRec) => {
        if (!productRec) return null;

        const { recommended_product_type, product_code, bias_addresses, rationale, confidence, alternative_products, risk_alignment } = productRec;

        return (
            <Card
                bg={cardBg}
                borderRadius="lg"
                boxShadow="md"
                borderWidth="2px"
                borderColor={isDarkTheme ? accentColor : primaryColor}
            >
                <CardBody>
                    <VStack spacing={4} align="stretch">
                        <Flex justify="space-between" align="center" flexWrap="wrap" gap={2}>
                            <Heading size="md" color={headingTextColor}>Product Recommendation</Heading>
                            <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                                {(confidence * 100).toFixed(0)}% Confidence
                            </Badge>
                        </Flex>

                        <Box bg={infoBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                            <HStack justify="space-between" mb={2} flexWrap="wrap">
                                <Heading size="sm" color={headingTextColor}>{recommended_product_type}</Heading>
                                <Badge colorScheme="blue" fontSize="sm">{product_code}</Badge>
                            </HStack>

                            {rationale && rationale.length > 0 && (
                                <VStack align="stretch" spacing={2} mt={3}>
                                    {rationale.map((item, index) => (
                                        <HStack key={index} align="start">
                                            <Icon as={CheckCircleIcon} color={successColor} mt={1} />
                                            <Text fontSize="sm" color={bodyTextColor}>{item}</Text>
                                        </HStack>
                                    ))}
                                </VStack>
                            )}
                        </Box>

                        {risk_alignment && (
                            <Box bg={successBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                                <Heading size="sm" mb={3} color={headingTextColor}>Risk Alignment</Heading>
                                <SimpleGrid columns={2} spacing={4}>
                                    <Stat>
                                        <StatLabel fontSize="xs" color={labelTextColor}>Status</StatLabel>
                                        <StatNumber>
                                            <Badge colorScheme={getRiskAlignmentColor(risk_alignment.status)} fontSize="md">
                                                {risk_alignment.status.toUpperCase()}
                                            </Badge>
                                        </StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel fontSize="xs" color={labelTextColor}>Product Risk</StatLabel>
                                        <StatNumber fontSize="lg" color={strongTextColor}>
                                            {risk_alignment.product_risk}/10
                                        </StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel fontSize="xs" color={labelTextColor}>Customer Tolerance</StatLabel>
                                        <StatNumber fontSize="lg" color={strongTextColor}>
                                            {risk_alignment.customer_tolerance}/10
                                        </StatNumber>
                                    </Stat>
                                    <Stat>
                                        <StatLabel fontSize="xs" color={labelTextColor}>Harmony Score</StatLabel>
                                        <StatNumber fontSize="lg" color={strongTextColor}>
                                            {(risk_alignment.harmony * 100).toFixed(0)}%
                                        </StatNumber>
                                    </Stat>
                                </SimpleGrid>
                            </Box>
                        )}

                        {bias_addresses && Object.keys(bias_addresses).length > 0 && (
                            <Box>
                                <Heading size="sm" mb={3} color={headingTextColor}>Biases Addressed</Heading>
                                <HStack spacing={3} flexWrap="wrap">
                                    {Object.entries(bias_addresses).map(([bias, score]) => (
                                        <Badge key={bias} colorScheme="purple" fontSize="sm" px={3} py={1}>
                                            {bias.replace(/_/g, ' ')}: {(score * 100).toFixed(0)}%
                                        </Badge>
                                    ))}
                                </HStack>
                            </Box>
                        )}

                        {alternative_products && alternative_products.length > 0 && (
                            <Box>
                                <Heading size="sm" mb={3} color={headingTextColor}>Alternative Products</Heading>
                                <VStack spacing={2} align="stretch">
                                    {alternative_products.map((alt, index) => (
                                        <Box
                                            key={index}
                                            bg={successBg}
                                            p={3}
                                            borderRadius="md"
                                            borderWidth="1px"
                                            borderColor={borderColor}
                                        >
                                            <HStack justify="space-between" mb={1} flexWrap="wrap">
                                                <Text fontWeight="bold" fontSize="sm" color={strongTextColor}>
                                                    {alt.product_type}
                                                </Text>
                                                <Badge colorScheme="blue" fontSize="xs">
                                                    Score: {(alt.score * 100).toFixed(0)}%
                                                </Badge>
                                            </HStack>
                                            <Text fontSize="xs" color={mutedTextColor}>{alt.product_code}</Text>
                                            <Text fontSize="xs" color={bodyTextColor} mt={1}>{alt.rationale}</Text>
                                        </Box>
                                    ))}
                                </VStack>
                            </Box>
                        )}
                    </VStack>
                </CardBody>
            </Card>
        );
    };

    // Render customer bias report
    const renderCustomerBiasReport = (customerId) => {
        if (loadingReports[customerId]) {
            return (
                <Flex justify="center" align="center" py={10}>
                    <Spinner size="xl" color={isDarkTheme ? accentColor : primaryColor} />
                </Flex>
            );
        }

        const reportData = customerReports[customerId];
        if (!reportData) {
            return <Text color={mutedTextColor}>No report data available.</Text>;
        }

        const { biases, radar, rationale, product_recommendation, scored_at } = reportData;

        return (
            <VStack spacing={6} align="stretch">
                {/* Product Recommendation */}
                {product_recommendation && renderProductRecommendation(product_recommendation)}

                {/* Radar Chart */}
                {radar && (
                    <Card
                        bg={cardBg}
                        borderRadius="lg"
                        boxShadow="md"
                        borderWidth="1px"
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <Heading size="md" mb={4} color={headingTextColor}>Bias Category Scores</Heading>
                            {renderRadarChart(radar)}
                        </CardBody>
                    </Card>
                )}

                {/* Bias Heatmap */}
                {biases && (
                    <Card
                        bg={cardBg}
                        borderRadius="lg"
                        boxShadow="md"
                        borderWidth="1px"
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <Heading size="md" mb={4} color={headingTextColor}>Behavioral Bias Heatmap</Heading>
                            <Text fontSize="sm" color={mutedTextColor} mb={4}>
                                Color intensity indicates bias severity. Hover over cells for detailed information.
                            </Text>
                            {renderBiasHeatmap(biases)}
                        </CardBody>
                    </Card>
                )}

                {/* Rationale */}
                {rationale && rationale.bullets && rationale.bullets.length > 0 && (
                    <Card bg={infoBg} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
                        <CardBody>
                            <Heading size="sm" mb={3} color={headingTextColor}>Key Insights</Heading>
                            <VStack align="stretch" spacing={2}>
                                {rationale.bullets.map((item, index) => (
                                    <HStack key={index} align="start">
                                        <Badge colorScheme="blue" fontSize="xs" mt={0.5}>{index + 1}</Badge>
                                        <Text fontSize="sm" color={bodyTextColor}>{item}</Text>
                                    </HStack>
                                ))}
                            </VStack>
                        </CardBody>
                    </Card>
                )}

                {/* Metadata */}
                <Card bg={successBg} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
                    <CardBody>
                        <Text fontSize="xs" color={mutedTextColor}>
                            Report generated at: {new Date(scored_at).toLocaleString()}
                        </Text>
                    </CardBody>
                </Card>
            </VStack>
        );
    };

    // Loading state
    if (!token || (!user && loading)) {
        return <Loading />;
    }

    // User not loaded
    if (!user) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center" bg={backgroundColor}>
                <Spinner size="xl" color={isDarkTheme ? accentColor : primaryColor} />
            </Box>
        );
    }

    // No file uploads yet
    if (user && user.noOfFileUploads === 0) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
                <Navbar />
                <UploadYourFile setUser={setUser} token={token} />
            </Box>
        );
    }

    // Check if user has access
    const hasAccess = user.subscription === 'business' &&
        (user.role === 'admin' || user.role === 'compliance');

    if (!hasAccess) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column">
                <Navbar />
                <Box display="flex" flex="1" bg={backgroundColor}>
                    <SideBar />
                    <Container maxW="container.xl" flex="1" py={8}>
                        <VStack spacing={6} align="center" justify="center" minH="60vh">
                            <Icon as={FaShieldAlt} boxSize={16} color={mutedTextColor} />
                            <Heading size="lg" color={headingTextColor}>
                                Audit & Compliance
                            </Heading>
                            <Text color={bodyTextColor} textAlign="center">
                                This feature is only available for Business subscription users with higher access than yours.
                            </Text>
                        </VStack>
                    </Container>
                </Box>
                <Footer />
            </Box>
        );
    }

    return (
        <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
            <Navbar />
            <Box display="flex" flex="1">
                <SideBar />
                <Container maxW="container.xl" flex="1" py={6}>
                    <VStack spacing={6} align="stretch">
                        {/* Free tier lock */}
                        {user.subscription === 'free' && (
                            <Box position="relative" mt={10}>
                                <Flex
                                    position="relative"
                                    minH="500px"
                                    zIndex={5}
                                    bg={overlayBg}
                                    backdropFilter="blur(3px)"
                                    borderRadius="xl"
                                    justify="center"
                                    align="center"
                                    flexDirection="column"
                                    textAlign="center"
                                    border="3px dashed"
                                    borderColor={isDarkTheme ? accentColor : primaryColor}
                                    p={8}
                                >
                                    <Icon
                                        as={FaLock}
                                        boxSize={16}
                                        color={isDarkTheme ? accentColor : primaryColor}
                                        mb={4}
                                    />
                                    <Heading size="xl" mb={3} color={headingTextColor}>
                                        Premium Reports & Exports
                                    </Heading>
                                    <Text color={bodyTextColor} maxW="md" mb={6} fontSize="lg" fontWeight="medium">
                                        Unlock comprehensive reports and data exports with FinanSee Premium. Export customer insights, behavioral analytics, and detailed reports in multiple formats.
                                    </Text>
                                    <Button
                                        size="lg"
                                        bg={primaryColor}
                                        color="white"
                                        onClick={() => navigate('/subscription')}
                                        mb={3}
                                        px={8}
                                        py={6}
                                        fontSize="lg"
                                        _hover={{ bg: buttonHoverBg }}
                                    >
                                        Upgrade to Premium
                                    </Button>
                                </Flex>
                            </Box>
                        )}

                        {/* Premium content - Admin view */}
                        {(user.subscription === 'pro' || user.subscription === 'business') && user.role === 'admin' && (
                            <>
                                {/* Header */}
                                <Box>
                                    <Heading size="lg" color={headingTextColor} mb={2}>
                                        Reports & Exports
                                    </Heading>
                                    <Text color={mutedTextColor} fontSize="sm">
                                        Download comprehensive reports and export customer data
                                    </Text>
                                </Box>

                                {/* Export Actions */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <Heading size="md" mb={4} color={headingTextColor}>Export Data</Heading>
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                            <Button
                                                leftIcon={<DownloadIcon />}
                                                bg={primaryColor}
                                                color="white"
                                                onClick={exportAllCustomersCSV}
                                                isLoading={exportingCSV}
                                                _hover={{ bg: buttonHoverBg }}
                                            >
                                                Export All Customers
                                            </Button>
                                            <Button
                                                leftIcon={<DownloadIcon />}
                                                colorScheme="purple"
                                                onClick={exportBiasScoresCSV}
                                                isLoading={exportingCSV}
                                            >
                                                Export Bias Scores
                                            </Button>
                                        </SimpleGrid>
                                    </CardBody>
                                </Card>

                                <Divider borderColor={borderColor} />

                                {/* Search Customers */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <Heading size="md" mb={4} color={headingTextColor}>Customer Reports</Heading>
                                        <HStack spacing={4}>
                                            <InputGroup flex={1}>
                                                <InputLeftElement pointerEvents="none">
                                                    <SearchIcon color={mutedTextColor} />
                                                </InputLeftElement>
                                                <Input
                                                    placeholder="Search customers..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                                    borderColor={borderColor}
                                                    bg={inputBg}
                                                    color={strongTextColor}
                                                    _hover={{ borderColor: isDarkTheme ? accentColor : primaryColor }}
                                                    _focus={{
                                                        borderColor: isDarkTheme ? accentColor : primaryColor,
                                                        boxShadow: `0 0 0 1px ${isDarkTheme ? accentColor : primaryColor}`
                                                    }}
                                                    _placeholder={{ color: mutedTextColor }}
                                                />
                                            </InputGroup>
                                            <Button
                                                bg={primaryColor}
                                                color="white"
                                                onClick={handleSearch}
                                                _hover={{ bg: buttonHoverBg }}
                                                flexShrink={0}
                                            >
                                                Search
                                            </Button>
                                        </HStack>
                                    </CardBody>
                                </Card>

                                {/* Customer List */}
                                {loading ? (
                                    <Flex justify="center" align="center" py={20}>
                                        <Spinner size="xl" color={isDarkTheme ? accentColor : primaryColor} />
                                    </Flex>
                                ) : customers.length === 0 ? (
                                    <Card
                                        bg={cardBg}
                                        borderRadius="lg"
                                        boxShadow="md"
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                    >
                                        <CardBody>
                                            <Text textAlign="center" color={mutedTextColor} py={10}>
                                                No customers found.
                                            </Text>
                                        </CardBody>
                                    </Card>
                                ) : (
                                    <VStack spacing={4} align="stretch">
                                        {customers.map((customer) => (
                                            <Card
                                                key={customer.customer_id || customer.id}
                                                bg={cardBg}
                                                borderRadius="lg"
                                                boxShadow="md"
                                                transition="all 0.2s"
                                                _hover={{ boxShadow: 'lg', transform: 'translateY(-2px)' }}
                                                borderWidth="1px"
                                                borderColor={borderColor}
                                            >
                                                <CardBody>
                                                    <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
                                                        <VStack align="start" spacing={1} flex={1}>
                                                            <Heading size="sm" color={headingTextColor}>
                                                                {customer.customer_id || customer.name || 'Unknown Customer'}
                                                            </Heading>
                                                            <HStack spacing={4} flexWrap="wrap">
                                                                {customer.rm_name && (
                                                                    <Text fontSize="sm" color={mutedTextColor}>
                                                                        RM: {customer.rm_name}
                                                                    </Text>
                                                                )}
                                                                {customer.email && (
                                                                    <Text fontSize="sm" color={mutedTextColor}>
                                                                        {customer.email}
                                                                    </Text>
                                                                )}
                                                            </HStack>
                                                        </VStack>
                                                        <HStack spacing={2}>
                                                            <Button
                                                                size="sm"
                                                                leftIcon={<DownloadIcon />}
                                                                colorScheme="green"
                                                                onClick={() => downloadCustomerPDFReport(customer.customer_id || customer.id)}
                                                                isLoading={downloadingPDF[customer.customer_id || customer.id]}
                                                            >
                                                                PDF Report
                                                            </Button>
                                                            <IconButton
                                                                icon={
                                                                    expandedCustomer === (customer.customer_id || customer.id) ? (
                                                                        <ChevronUpIcon />
                                                                    ) : (
                                                                        <ChevronDownIcon />
                                                                    )
                                                                }
                                                                onClick={() => toggleCustomer(customer.customer_id || customer.id)}
                                                                colorScheme="blue"
                                                                variant="ghost"
                                                                aria-label="Toggle details"
                                                            />
                                                        </HStack>
                                                    </Flex>

                                                    <Collapse
                                                        in={expandedCustomer === (customer.customer_id || customer.id)}
                                                        animateOpacity
                                                    >
                                                        <Box mt={6} pt={6} borderTop="1px solid" borderColor={borderColor}>
                                                            {renderCustomerBiasReport(customer.customer_id || customer.id)}
                                                        </Box>
                                                    </Collapse>
                                                </CardBody>
                                            </Card>
                                        ))}
                                    </VStack>
                                )}

                                {/* Pagination */}
                                {!loading && customers.length > 0 && (
                                    <Card
                                        bg={cardBg}
                                        borderRadius="lg"
                                        boxShadow="md"
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                    >
                                        <CardBody>
                                            <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
                                                <Text fontSize="sm" color={mutedTextColor}>
                                                    Showing {customers.length} of {pagination.total_count} customers
                                                </Text>
                                                <HStack>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => fetchCustomers(pagination.page - 1, searchTerm)}
                                                        isDisabled={pagination.page === 1}
                                                        variant="outline"
                                                        borderColor={borderColor}
                                                        color={strongTextColor}
                                                        _hover={{ bg: hoverBg }}
                                                    >
                                                        Previous
                                                    </Button>
                                                    <Text fontSize="sm" color={mutedTextColor}>
                                                        Page {pagination.page}
                                                    </Text>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => fetchCustomers(pagination.page + 1, searchTerm)}
                                                        isDisabled={customers.length < pagination.page_size}
                                                        variant="outline"
                                                        borderColor={borderColor}
                                                        color={strongTextColor}
                                                        _hover={{ bg: hoverBg }}
                                                    >
                                                        Next
                                                    </Button>
                                                </HStack>
                                            </Flex>
                                        </CardBody>
                                    </Card>
                                )}
                            </>
                        )}

                        {(user.subscription === 'pro' || user.subscription === 'business') && user.role === 'compliance' && (
                            <>
                                {/* Header */}
                                <Box>
                                    <Heading size="lg" color={headingTextColor} mb={2}>
                                        Reports & Exports
                                    </Heading>
                                    <Text color={mutedTextColor} fontSize="sm">
                                        Download comprehensive reports and export customer data
                                    </Text>
                                </Box>

                                {/* Export Actions */}
                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <Heading size="md" mb={4} color={headingTextColor}>Export Data</Heading>
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                            <Button
                                                leftIcon={<DownloadIcon />}
                                                bg={primaryColor}
                                                color="white"
                                                onClick={exportAllCustomersCSV}
                                                isLoading={exportingCSV}
                                                _hover={{ bg: buttonHoverBg }}
                                            >
                                                Export All Customers
                                            </Button>
                                            <Button
                                                leftIcon={<DownloadIcon />}
                                                colorScheme="purple"
                                                onClick={exportBiasScoresCSV}
                                                isLoading={exportingCSV}
                                            >
                                                Export Bias Scores
                                            </Button>
                                        </SimpleGrid>
                                    </CardBody>
                                </Card>

                                <Divider borderColor={borderColor} />
                            </>
                        )}

                        {/* Premium content - RM view */}
                        {(user.subscription === 'pro' || user.subscription === 'business') && user.role === 'rm' && (
                            <>
                                <Box>
                                    <Heading size="lg" color={headingTextColor} mb={2}>
                                        My Reports & Exports
                                    </Heading>
                                    <Text color={mutedTextColor} fontSize="sm">
                                        Download reports for your customers
                                    </Text>
                                </Box>

                                <Card
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <Heading size="md" mb={4} color={headingTextColor}>Export My Data</Heading>
                                        <Button
                                            leftIcon={<DownloadIcon />}
                                            bg={primaryColor}
                                            color="white"
                                            onClick={exportMyCustomersCSV}
                                            isLoading={exportingCSV}
                                            _hover={{ bg: buttonHoverBg }}
                                        >
                                            Export My Customers
                                        </Button>
                                    </CardBody>
                                </Card>
                            </>
                        )}
                    </VStack>
                </Container>
            </Box>
            <Footer />
        </Box>
    );
}

export default ReportsExports;